import { appendDebugLog } from "../utils/debugLog.js";

const dummyCanvasElement = document.createElement("canvas");
const dummyCanvasContext = dummyCanvasElement.getContext("2d", {
  willReadFrequently: true,
});
let cameraKillProcessID: ReturnType<typeof setTimeout> | undefined;

function stopStream(videoElement: HTMLVideoElement) {
  const src = videoElement.srcObject as MediaStream | null;
  if (src) {
    try {
      appendDebugLog("camera", "stop stream", {
        tracks: src.getTracks().map((track) => ({
          kind: track.kind,
          readyState: track.readyState,
        })),
      });
      src.getTracks().forEach((t) => t.stop());
    } catch {
      // no-op
    }
  }
  videoElement.srcObject = null;
}

function getLiveStream(videoElement: HTMLVideoElement) {
  const src = videoElement.srcObject as MediaStream | null;
  if (!src) return undefined;

  const hasLiveVideoTrack = src
    .getVideoTracks()
    .some((track) => track.readyState === "live");
  return hasLiveVideoTrack ? src : undefined;
}

export const getQR = async (
  videoElement: HTMLVideoElement,
  canvasElement?: HTMLCanvasElement
) => {
  void canvasElement;

  if (cameraKillProcessID) {
    window.clearTimeout(cameraKillProcessID);
    cameraKillProcessID = undefined;
    appendDebugLog("camera", "cancel scheduled stream stop");
  }

  const constraints: MediaStreamConstraints = {
    video: {
      width: { ideal: 640 },
      height: { ideal: 480 },
      facingMode: "environment",
    },
    audio: false,
  };

  const liveStream = getLiveStream(videoElement);
  appendDebugLog("camera", liveStream ? "reuse live stream" : "request stream");

  let stream: MediaStream;
  try {
    stream =
      liveStream || (await navigator.mediaDevices.getUserMedia(constraints));
    appendDebugLog("camera", "stream ready", {
      videoTracks: stream.getVideoTracks().length,
    });
  } catch (error) {
    appendDebugLog("camera", "getUserMedia failed", error);
    throw error;
  }

  // Prepare the video element for reliable iOS playback
  videoElement.muted = true;
  videoElement.setAttribute("playsinline", "true");
  videoElement.style.display = "block";
  videoElement.srcObject = stream;

  // Wait for metadata, then play, then wait until actually playing
  await new Promise<void>((resolve) => {
    if (videoElement.readyState >= 1) resolve();
    else
      videoElement.addEventListener("loadedmetadata", () => resolve(), {
        once: true,
      });
  });
  await videoElement.play().catch((error) => {
    appendDebugLog("camera", "video play failed", error);
    // If play fails (e.g., no gesture), we still proceed; detection loop guards for dimensions
  });
  await new Promise<void>((resolve) => {
    if (videoElement.readyState >= 2 && !videoElement.paused) return resolve();
    const onPlaying = () => resolve();
    videoElement.addEventListener("playing", onPlaying, { once: true });
    // Fallback timeout in case "playing" never fires
    setTimeout(() => resolve(), 500);
  });

  const track = stream.getVideoTracks()[0];
  const { data, reject } = startDetectionLoop(videoElement);
  appendDebugLog("camera", "QR detection loop started");

  // If the system signals the track ends (iOS background/home), cleanly reject and stop
  const onTrackEnded = () => {
    appendDebugLog("camera", "track ended");
    reject?.("camera_ended");
  };
  track?.addEventListener("ended", onTrackEnded, { once: true });

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      appendDebugLog("camera", "page hidden");
      reject?.("page_hidden");
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange, { once: true });

  data.finally(() => {
    videoElement.pause();
    videoElement.style.display = "none";
    try {
      track?.removeEventListener("ended", onTrackEnded);
    } catch {
      // no-op
    }
    try {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    } catch {
      // no-op
    }
    cameraKillProcessID = window.setTimeout(() => {
      stopStream(videoElement);
      cameraKillProcessID = undefined;
    }, 30000);
    appendDebugLog("camera", "scheduled stream stop", { delayMs: 30000 });
  });

  return { data, reject };
};
function startDetectionLoop(videoElement: HTMLVideoElement) {
  let detectionInterval: ReturnType<typeof setInterval> | undefined;

  let rejectWithCleanup!: (reason?: unknown) => void;
  const data = new Promise((resolve, reject) => {
    rejectWithCleanup = (reason?: unknown) => {
      if (detectionInterval) {
        clearInterval(detectionInterval);
        detectionInterval = undefined;
      }
      reject(reason);
    };

    detectionInterval = setInterval(() => {
      // Guard: wait until dimensions are available (prevents iOS paused track errors)
      const vw = videoElement.videoWidth;
      const vh = videoElement.videoHeight;
      if (!vw || !vh || videoElement.paused) return;

      dummyCanvasElement.width = vw;
      dummyCanvasElement.height = vh;
      const ctx = dummyCanvasContext;
      if (!ctx) return;
      try {
        ctx.drawImage(
          videoElement,
          0,
          0,
          dummyCanvasElement.width,
          dummyCanvasElement.height
        );
      } catch {
        return; // Skip this tick if draw fails
      }

      const imageData = ctx.getImageData(
        0,
        0,
        dummyCanvasElement.width,
        dummyCanvasElement.height
      );
      const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

      if (qrCode) {
        if (detectionInterval) clearInterval(detectionInterval);
        appendDebugLog("camera", "QR detected", {
          matrixWidth: qrCode.matrix.width,
        });
        resolve(qrCode.matrix.data);
      }
    }, 500);
  });

  return { data, reject: rejectWithCleanup };
}
